import { Navigate, createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Users } from "lucide-react";
import { useMemo, useState } from "react";

import { Avatar } from "@/components/avatar";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/hooks/use-auth";
import { listUsers, updateUserRole } from "@/lib/api/users";
import { ApiError } from "@/lib/api-client";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";
import type { ManagedUser, UserRole } from "@/types";

export const Route = createFileRoute("/_app/admin/users")({
  component: UserManagementPage,
});

const ROLE_OPTIONS: { value: UserRole; label: string; hint: string }[] = [
  { value: "stakeholder", label: "Stakeholder", hint: "Submit & track own requests" },
  { value: "product", label: "Product", hint: "Review, decide & order the queue" },
  { value: "admin", label: "Admin", hint: "Everything, plus user management" },
];

const ROLE_BADGE: Record<UserRole, string> = {
  stakeholder: "border-border bg-muted/60 text-muted-foreground",
  product: "border-info/25 bg-info-background text-info",
  admin: "border-destructive/25 bg-destructive/10 text-destructive",
};

function UserManagementPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [q, setQ] = useState("");
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: ["admin", "users"],
    queryFn: listUsers,
    enabled: user?.role === "admin",
  });

  const mutation = useMutation({
    mutationFn: (input: { id: number; role: UserRole }) => updateUserRole(input.id, input.role),
    onSuccess: async (updated) => {
      setError(null);
      queryClient.setQueryData<ManagedUser[]>(["admin", "users"], (prev) =>
        (prev ?? []).map((u) => (u.id === updated.id ? updated : u)),
      );
      await queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Failed to change the role. Please try again.");
    },
    onSettled: () => setPendingId(null),
  });

  const rows = useMemo(() => {
    const all = usersQuery.data ?? [];
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((u) =>
      [u.display_name, u.username, u.email].some((v) => v.toLowerCase().includes(needle)),
    );
  }, [usersQuery.data, q]);

  const roleCounts = useMemo(() => {
    const counts: Record<UserRole, number> = { stakeholder: 0, product: 0, admin: 0 };
    for (const u of usersQuery.data ?? []) counts[u.role] += 1;
    return counts;
  }, [usersQuery.data]);

  if (user && user.role !== "admin") {
    return <Navigate to="/" />;
  }

  return (
    <div className="grid min-w-0 gap-5">
      <PageHeader
        title="User management"
        description="Control who can do what in Open Backlog. New users appear here after their first Nexus sign-in and start as Stakeholder."
      />

      <section aria-label="Roles summary" className="grid grid-cols-3 overflow-hidden rounded-xl bg-card shadow-xs ring-1 ring-foreground/10">
        {ROLE_OPTIONS.map((role, i) => (
          <div key={role.value} className={cn("px-4 py-3.5", i < 2 && "border-r border-border")}>
            <p className="text-[11px] font-medium tracking-wider text-muted-foreground uppercase">{role.label}</p>
            <p className="mt-1 text-[22px] leading-none font-semibold tracking-tight tabular-nums">{roleCounts[role.value]}</p>
            <p className="mt-1.5 truncate text-xs text-muted-foreground">{role.hint}</p>
          </div>
        ))}
      </section>

      {error ? <p className="rounded-md border border-destructive/30 bg-destructive/6 px-3 py-2 text-[13px] text-destructive">{error}</p> : null}

      <section className="min-w-0 overflow-hidden rounded-xl bg-card shadow-xs ring-1 ring-foreground/10" aria-label="Users">
        <div className="border-b border-border px-3 py-3 sm:px-4">
          <div className="relative max-w-sm">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, username, or email…" className="h-9 pl-8" aria-label="Search users" />
            {q ? <button type="button" onClick={() => setQ("")} className="absolute top-1/2 right-2 -translate-y-1/2 rounded px-1 text-xs text-muted-foreground hover:text-foreground">Clear</button> : null}
          </div>
        </div>

        {usersQuery.isLoading ? (
          <div className="divide-y divide-border" aria-busy="true" aria-label="Loading users">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex animate-pulse items-center gap-3 px-4 py-3.5">
                <div className="size-8 rounded-full bg-muted" />
                <div className="min-w-0 flex-1 space-y-2"><div className="h-3.5 w-1/4 rounded bg-muted" /><div className="h-3 w-2/5 rounded bg-muted" /></div>
                <div className="h-8 w-32 rounded bg-muted" />
              </div>
            ))}
          </div>
        ) : usersQuery.isError ? (
          <div className="px-4 py-12 text-center">
            <p className="text-sm font-medium text-destructive">Failed to load users.</p>
            <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => void usersQuery.refetch()}>Retry</Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <span className="mx-auto flex size-9 items-center justify-center rounded-full bg-muted text-muted-foreground"><Users className="size-4" /></span>
            <p className="mt-3 text-sm font-medium">{q ? "No matching users" : "No users yet"}</p>
            <p className="mx-auto mt-1 max-w-sm text-[13px] text-muted-foreground">
              {q ? "Try a different search." : "Users are created automatically the first time someone signs in with Nexus SSO."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((managed) => {
              const isSelf = managed.id === user?.id;
              return (
                <li key={managed.id} className="flex flex-wrap items-center gap-3 px-3 py-3 sm:px-4">
                  <Avatar name={managed.display_name || managed.username} className="size-8 text-[10px]" />
                  <div className="min-w-0 flex-1 basis-48">
                    <p className="flex flex-wrap items-center gap-2 text-[13px] font-medium">
                      <span className="truncate">{managed.display_name || managed.username}</span>
                      {isSelf ? <span className="inline-flex h-[20px] items-center rounded-full bg-muted px-1.5 text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">You</span> : null}
                      {!managed.is_active ? <span className="text-[11px] font-normal text-destructive">Inactive</span> : null}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">
                      {managed.email || managed.username} · joined <span className="tabular-nums">{formatDate(managed.created_at)}</span> · {managed.request_count} {managed.request_count === 1 ? "request" : "requests"}
                    </p>
                  </div>
                  <span className={cn("inline-flex h-[22px] shrink-0 items-center rounded-full border px-2 text-[11px] font-medium capitalize", ROLE_BADGE[managed.role])}>
                    {managed.role}
                  </span>
                  <Select
                    value={managed.role}
                    disabled={isSelf || pendingId === managed.id}
                    onValueChange={(value) => {
                      const role = value as UserRole;
                      if (role === managed.role) return;
                      setError(null);
                      setPendingId(managed.id);
                      mutation.mutate({ id: managed.id, role });
                    }}
                  >
                    <SelectTrigger className="w-40 shrink-0" aria-label={`Role for ${managed.display_name || managed.username}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map((role) => (
                        <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isSelf ? <span className="w-full text-xs text-muted-foreground sm:w-auto sm:basis-full">Your own role can't be changed here — ask another admin.</span> : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <p className="text-xs leading-relaxed text-muted-foreground">
        Role changes apply at the user's next sign-in or token refresh. There always has to be at least one admin — the last admin's role is locked.
      </p>
    </div>
  );
}
